import Box from '@mui/material/Box'
import ButtonBase from '@mui/material/ButtonBase'
import GlobalStyles from '@mui/material/GlobalStyles'
import Typography from '@mui/material/Typography'
import { alpha, useTheme } from '@mui/material/styles'
import { PHONE_TAB_BAR_HEIGHT } from '../constants'
import type { PhoneView } from '../utilities/nativeApp'

type IconProps = { selected: boolean }

// The three icons are drawn here, in the line weight and proportions of Apple's SF Symbols
// (list.bullet, point.3.connected.trianglepath.dotted, map): a 24 pt grid, round caps, and a
// filled form for the selected tab, as an iOS tab bar does.

function ListIcon({ selected }: IconProps) {
  return (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" aria-hidden>
      {[6, 12, 18].map(y => (
        <g key={y}>
          <circle cx="4.75" cy={y} r={selected ? 1.9 : 1.6} fill="currentColor" />
          <line
            x1="9"
            y1={y}
            x2="20.5"
            y2={y}
            stroke="currentColor"
            strokeWidth={selected ? 2.2 : 1.8}
            strokeLinecap="round"
          />
        </g>
      ))}
    </svg>
  )
}

function GraphIcon({ selected }: IconProps) {
  const nodes = [
    [12, 4.75],
    [4.75, 18.25],
    [19.25, 18.25],
  ]
  return (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M12 4.75 4.75 18.25h14.5Z"
        stroke="currentColor"
        strokeWidth={selected ? 1.9 : 1.6}
        strokeLinejoin="round"
        strokeDasharray={selected ? undefined : '0.1 3'}
        strokeLinecap="round"
      />
      {nodes.map(([cx, cy]) => (
        <circle
          key={`${cx}-${cy}`}
          cx={cx}
          cy={cy}
          r="2.9"
          fill={selected ? 'currentColor' : 'var(--tab-bar-bg)'}
          stroke="currentColor"
          strokeWidth="1.7"
        />
      ))}
    </svg>
  )
}

function MapIcon({ selected }: IconProps) {
  return (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M3 6.6 8.8 4.2l6.4 2.4L21 4.2v13.2l-5.8 2.4-6.4-2.4L3 19.8Z"
        fill="currentColor"
        fillOpacity={selected ? 0.22 : 0}
        stroke="currentColor"
        strokeWidth={selected ? 1.9 : 1.6}
        strokeLinejoin="round"
      />
      <path
        d="M8.8 4.2v13.2M15.2 6.6v13.2"
        stroke="currentColor"
        strokeWidth={selected ? 1.9 : 1.6}
        strokeLinecap="round"
      />
    </svg>
  )
}

const TABS: { view: PhoneView; label: string; Icon: (p: IconProps) => React.JSX.Element }[] = [
  { view: 'list', label: 'List', Icon: ListIcon },
  { view: 'graph', label: 'Graph', Icon: GraphIcon },
  { view: 'map', label: 'Map', Icon: MapIcon },
]

type PhoneTabBarProps = { view: PhoneView; onChange: (view: PhoneView) => void }

/**
 * The bar at the bottom of the noBGP app on a phone: List, Graph, Map, the way Instagram and the
 * iOS apps put their sections. A translucent bar with a hairline on top, the selected tab in the
 * accent colour and the others grey, and its own padding over the home indicator.
 */
export default function PhoneTabBar({ view, onChange }: Readonly<PhoneTabBarProps>) {
  const { palette } = useTheme()
  const background = alpha(palette.background.default, 0.86)

  return (
    <>
      {/* The map's zoom buttons and credits (maplibre, bottom right) sit above the bar. */}
      <GlobalStyles
        styles={{
          // ⚠ `body` and !important: maplibre's own stylesheet sets `bottom: 0` on these with the
          // same specificity and loads after this one (mapCore.ts imports it with the map).
          'body .maplibregl-ctrl-bottom-right, body .maplibregl-ctrl-bottom-left': {
            bottom: `calc(${PHONE_TAB_BAR_HEIGHT}px + env(safe-area-inset-bottom)) !important`,
          },
        }}
      />
      <Box
        component="nav"
        aria-label="Views"
        sx={{
          '--tab-bar-bg': palette.background.default,
          position: 'fixed',
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 1350, // over the drawer (1300) and the graph, under dialogs and terminals
          display: 'flex',
          height: `calc(${PHONE_TAB_BAR_HEIGHT}px + env(safe-area-inset-bottom))`,
          paddingBottom: 'env(safe-area-inset-bottom)',
          background,
          backdropFilter: 'saturate(180%) blur(20px)',
          WebkitBackdropFilter: 'saturate(180%) blur(20px)',
          borderTop: `0.5px solid ${alpha(palette.text.primary, 0.18)}`,
        }}
      >
        {TABS.map(({ view: tab, label, Icon }) => {
          const selected = tab === view
          return (
            <ButtonBase
              key={tab}
              aria-current={selected ? 'page' : undefined}
              onClick={() => onChange(tab)}
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                gap: '2px',
                color: selected ? 'primary.main' : 'text.secondary',
                WebkitTapHighlightColor: 'transparent',
              }}
            >
              <Icon selected={selected} />
              <Typography component="span" sx={{ fontSize: 10, fontWeight: 600, letterSpacing: 0.1, lineHeight: 1 }}>
                {label}
              </Typography>
            </ButtonBase>
          )
        })}
      </Box>
    </>
  )
}
